import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { impersonationInfo, endImpersonation, ownHome } from '../utils/impersonation';

const RED = '#B91C1C';

// ============================================
// IMPERSONATION BANNER — "you are not yourself right now"
// ============================================
// Fixed across the top of every screen while a super admin is viewing the portal as somebody else.
// It has to be impossible to miss: an impersonated session can do real things in that person's
// name, so the bar states who you are, who started it, when it ends, and gives one obvious way out.
// The body gets padded so it never covers a page's own header.
// ============================================
export default function ImpersonationBanner() {
  const [info] = useState(() => impersonationInfo());
  const [leaving, setLeaving] = useState(false);

  const endsAt = (info && info.started_at && info.expires_in)
    ? new Date(new Date(info.started_at).getTime() + info.expires_in * 1000)
    : null;

  useEffect(() => {
    document.body.style.paddingTop = info ? '42px' : '';
    return () => { document.body.style.paddingTop = ''; };
  }, [info]);

  const exit = async () => {
    setLeaving(true);
    // Record the end while we still hold the impersonated token, so the trail has both ends.
    try {
      await fetch(`${API_URL}/auth/impersonate/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ by_email: (info.by && info.by.email) || null })
      });
    } catch (err) {
      console.warn('Could not tell the backend the view-as session ended:', err);
    }
    endImpersonation();
    // A full reload rather than a client-side navigate: every screen must re-read the session it
    // now has, otherwise a stale component could still be showing the other person's data.
    window.location.href = ownHome();
  };

  // Hard stop. The impersonated app token lives an hour; when it dies, so does the view.
  useEffect(() => {
    if (!info || !endsAt) return undefined;
    const ms = endsAt.getTime() - Date.now();
    if (ms <= 0) { exit(); return undefined; }
    const timer = setTimeout(() => { exit(); }, Math.min(ms, 2147483647));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  if (!info) return null;

  const as = info.as || {};

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2147483000,
      backgroundColor: RED, color: '#FFFFFF',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 12, padding: '8px 14px', fontSize: 13, flexWrap: 'wrap',
      boxShadow: '0 2px 10px rgba(0,0,0,0.28)'
    }}>
      <span>
        👁 <strong>Viewing as {as.full_name || as.email}</strong>
        {as.role ? ` (${String(as.role).replace(/_/g, ' ')})` : ''}
      </span>
      <span style={{ opacity: 0.85 }}>
        started by {(info.by && info.by.email) || 'a super admin'}
        {endsAt ? ` · ends ${endsAt.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}` : ''}
      </span>
      <button
        type="button"
        onClick={exit}
        disabled={leaving}
        style={{
          backgroundColor: '#FFFFFF', color: RED, border: 'none', borderRadius: 6,
          padding: '4px 12px', fontSize: 12, fontWeight: 700,
          cursor: leaving ? 'wait' : 'pointer', opacity: leaving ? 0.7 : 1
        }}
      >
        {leaving ? 'Exiting…' : 'Exit view-as'}
      </button>
    </div>
  );
}
